import { useMutation, useQueryClient } from "react-query";
import { useAccessToken } from "hooks/useAccessToken";
import { BACKEND_DOMAIN } from "utils/constants";
import { buildRequestHeaders } from "utils/helpers";
import AuditMarkdownField from "../AuditMarkdownField";
import styles from "../VirtualAuditPage.module.scss";
import tableStyles from "./DataInputsSection.module.scss";

interface DataInputsSectionProps {
    buildingModel: Building | null;
    property: {
        propertyUid: string;
        buildingModels?: PropertyBuildingModel[];
        virtualAuditDataCaveats?: string | null;
        virtualAuditImplementationConsiderations?: string | null;
        virtualAuditNextSteps?: string | null;
    } | null;
}

const DEFAULT_DATA_CAVEATS = `<ul><li>Audette collected equipment data from interviews with site teams &amp; equipment inventories</li><li>Landlord utility cost recovery of ~90% OpEx recovery rate estimated by property management</li><li>LED lighting coverage not specified → assumed 25% coverage</li><li>Age and ventilation rates not specified for AHUs &amp; RTUs → inferred the ventilation rates based on ASHRAE 62.1</li></ul>`;

const DEFAULT_IMPLEMENTATION_CONSIDERATIONS = `<p><strong>Rooftop Solar</strong></p><ul><li>The landlord will need to engage tenants' in power purchase agreements to off take the power in order to monetize the onsite solar</li><li>Confirm roof structural capacity for new load (2-4 lbs psf)</li><li>Verify compensation structure for excess power production - multiple tariffs offered by utilities</li></ul><p><strong>RTU Controls</strong></p><ul><li>Where allowable under the lease, aggregate controllers to a central head end. Co-benefit for landlord to get visibility into CRU and preventative maintenance requirements to extend equipment useful life.</li><li>Costing is based on controller and thermostat upgrades, whereas it may be possible to re-sequence certain RTUs once on-site inspection is conducted.</li></ul>`;

const DEFAULT_NEXT_STEPS = `<ul><li>Get feedback on projects from asset and property operations → incorporate edits to remodel property and refresh the plan as needed:<ul><li>Identify which projects are accretive vs. dilutive</li><li>Align projects to asset-specific investment goals, investors &amp; operational constraints</li><li>Quantify value uplift and IRR on incremental CapEx</li></ul></li><li>Freeform notes for SE<p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p><p>Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum</p></li></ul>`;

function DataInputsSection({ buildingModel, property }: DataInputsSectionProps) {
    const accessToken = useAccessToken();
    const queryClient = useQueryClient();

    const { mutate: save, isLoading: isSaving } = useMutation({
        mutationFn: async (payload: Record<string, string | null>) => {
            const res = await fetch(`${BACKEND_DOMAIN}/properties/${property?.propertyUid}`, {
                method: "PATCH",
                headers: { ...buildRequestHeaders(accessToken), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!res.ok) throw new Error(`Error ${res.status}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["property", property?.propertyUid]);
        },
    });

    const buildingModels = property?.buildingModels ?? [];
    const totalBuildings = buildingModels.length;

    const utilityCount = buildingModels.filter((bm) => bm.dataCoverage?.utilityDataUploaded).length;
    const equipmentCount = buildingModels.filter((bm) => bm.dataCoverage?.equipmentDataUploaded).length;

    const utilityRateProvided =
        buildingModel?.defaultElectricityUtilityRate != null ||
        buildingModel?.defaultNaturalGasUtilityRate != null ||
        buildingModel?.defaultSteamUtilityRate != null;

    const landlordAllocationProvided =
        buildingModel?.defaultLandlordShareCost != null ||
        buildingModel?.defaultLandlordShareElectricity != null ||
        buildingModel?.defaultLandlordShareNaturalGas != null ||
        buildingModel?.defaultLandlordShareSteam != null;

    const checkRows = [
        ...(buildingModel != null
            ? [{ label: "Utility data provided?", value: buildingModel.utilityDataUploaded ? "Yes" : "No" }]
            : []),
        ...(totalBuildings > 0
            ? [{ label: "Utility data coverage", value: `${utilityCount}/${totalBuildings} buildings` }]
            : []),
        {
            label: "Utility rate provided?",
            value: utilityRateProvided ? "Yes" : "No - regional average applied",
        },
        ...(buildingModel != null
            ? [{ label: "Equipment data provided?", value: buildingModel.equipmentDataUploaded ? "PCA" : "No" }]
            : []),
        ...(totalBuildings > 0
            ? [{ label: "Equipment data coverage", value: `${equipmentCount}/${totalBuildings} buildings` }]
            : []),
        {
            label: "Landlord-Tenant utility allocation provided?",
            value: landlordAllocationProvided ? "Yes" : "No",
        },
    ];

    return (
        <div>
            <h2 className={styles.sectionTitle}>Data Inputs Used</h2>

            <table className={tableStyles.table}>
                <thead>
                    <tr>
                        <th className={tableStyles.headerCell}>Data check</th>
                        <th className={tableStyles.headerCell} />
                    </tr>
                </thead>
                <tbody>
                    {checkRows.map((row) => (
                        <tr key={row.label}>
                            <td className={tableStyles.cell}>{row.label}</td>
                            <td className={tableStyles.cell}>{row.value}</td>
                        </tr>
                    ))}
                </tbody>
            </table>

            <AuditMarkdownField
                label="Data Caveats"
                value={property?.virtualAuditDataCaveats}
                defaultContent={DEFAULT_DATA_CAVEATS}
                onSave={(html) => save({ virtualAuditDataCaveats: html })}
                isSaving={isSaving}
            />
            <AuditMarkdownField
                label="Implementation Considerations"
                value={property?.virtualAuditImplementationConsiderations}
                defaultContent={DEFAULT_IMPLEMENTATION_CONSIDERATIONS}
                onSave={(html) => save({ virtualAuditImplementationConsiderations: html })}
                isSaving={isSaving}
            />
            <AuditMarkdownField
                label="Next Steps"
                value={property?.virtualAuditNextSteps}
                defaultContent={DEFAULT_NEXT_STEPS}
                onSave={(html) => save({ virtualAuditNextSteps: html })}
                isSaving={isSaving}
            />
        </div>
    );
}

export default DataInputsSection;
